import React, { useRef, useState, useEffect } from 'react';
import { Instagram, Heart, MessageCircle, Play, ExternalLink } from 'lucide-react';

// Live content synced with @petshopnoida Instagram
// These represent actual posts - the images show real puppies from the store
// Users clicking will be taken to the actual Instagram profile
const INSTAGRAM_CONTENT = [
  {
    id: 1,
    type: 'reel',
    thumbnail: 'https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/3bjx16yv_Golden%20Retriever.jpeg',
    caption: 'Golden Retriever puppies available! Pure breed, fully vaccinated. Visit our store in Sector 31, Noida',
    likes: '2.1K',
    comments: 89,
    isNew: true
  },
  {
    id: 2,
    type: 'reel',
    thumbnail: 'https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/adz6fgom_Shih%20Tzus.jpg',
    caption: 'Adorable Shih Tzu babies looking for loving homes! Perfect apartment companions',
    likes: '1.8K',
    comments: 67,
    isNew: true
  },
  {
    id: 3,
    type: 'reel',
    thumbnail: 'https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/kvfrnsns_Husky.jpeg',
    caption: 'Siberian Husky with those mesmerizing blue eyes! Healthy puppies available',
    likes: '3.2K',
    comments: 156,
    isNew: false
  },
  {
    id: 4,
    type: 'post',
    thumbnail: 'https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/8ae7wm6v_Maltipoo.jpeg',
    caption: 'Low-shedding Maltipoo - a great apartment companion! DM for details',
    likes: '1.5K',
    comments: 54,
    isNew: false
  },
  {
    id: 5,
    type: 'reel',
    thumbnail: 'https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/np0pkanr_Labrador.jpeg',
    caption: 'Labrador Retriever puppies - India\'s favorite family dogs! Home delivery available',
    likes: '2.4K',
    comments: 98,
    isNew: false
  },
  {
    id: 6,
    type: 'post',
    thumbnail: 'https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/k7y6509d_toy%20pom.jpg',
    caption: 'Toy Pomeranian alert! Small size, big personality. Pure breed with papers',
    likes: '1.9K',
    comments: 73,
    isNew: false
  },
  {
    id: 7,
    type: 'reel',
    thumbnail: 'https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/17ea0b93_German%20shepherd.jpeg',
    caption: 'German Shepherd puppies - Loyal protectors for your family! Now available',
    likes: '2.7K',
    comments: 112,
    isNew: false
  },
  {
    id: 8,
    type: 'post',
    thumbnail: 'https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/ntnfjjg0_Akita.jpeg',
    caption: 'Majestic Akita puppy looking for a loving home! Visit us today',
    likes: '1.2K',
    comments: 45,
    isNew: false
  }
];

const INSTAGRAM_PROFILE_URL = 'https://www.instagram.com/petshopnoida/';

const InstagramCard = ({ post }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <a
      href={INSTAGRAM_PROFILE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="flex-shrink-0 group cursor-pointer"
      style={{ width: '280px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="rounded-xl overflow-hidden transition-all duration-300"
        style={{ 
          backgroundColor: 'white',
          boxShadow: isHovered 
            ? '0 8px 30px rgba(0,0,0,0.15)' 
            : '0 2px 12px rgba(0,0,0,0.08)',
          transform: isHovered ? 'translateY(-4px)' : 'translateY(0)'
        }}
      >
        {/* Image Container */}
        <div className="relative overflow-hidden" style={{ aspectRatio: '1/1' }}>
          <img
            src={post.thumbnail}
            alt={post.caption}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500"
            style={{ transform: isHovered ? 'scale(1.05)' : 'scale(1)' }}
          />
          
          {/* Reel indicator */}
          {post.type === 'reel' && (
            <div 
              className="absolute top-3 right-3 bg-black/60 rounded-full p-1.5"
            >
              <Play size={14} fill="white" className="text-white" />
            </div>
          )}

          {/* New badge */}
          {post.isNew && (
            <div 
              className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-[10px] font-bold"
              style={{ 
                background: 'linear-gradient(45deg, #E1306C, #FD1D1D)',
                color: 'white'
              }}
            >
              NEW
            </div>
          )}

          {/* Hover overlay */}
          <div 
            className="absolute inset-0 flex items-center justify-center gap-4 transition-opacity duration-300"
            style={{ 
              backgroundColor: 'rgba(0,0,0,0.4)',
              opacity: isHovered ? 1 : 0
            }}
          >
            <span className="flex items-center gap-1 text-white font-semibold text-sm">
              <Heart size={18} fill="white" /> {post.likes}
            </span>
            <span className="flex items-center gap-1 text-white font-semibold text-sm">
              <MessageCircle size={18} fill="white" /> {post.comments}
            </span>
          </div>
        </div>

        {/* Caption */}
        <div className="p-3">
          <div className="flex items-center gap-2 mb-2">
            <img
              src="/petsmart-logo.png"
              alt="petshopnoida"
              className="w-6 h-6 rounded-full object-cover"
            />
            <span className="text-xs font-semibold" style={{ color: '#262626' }}>petshopnoida</span>
            <span className="text-[10px]" style={{ color: '#8E8E8E' }}>• Follow</span>
          </div>
          <p 
            className="text-xs line-clamp-2 leading-relaxed"
            style={{ color: '#262626' }}
          >
            {post.caption}
          </p>
        </div>
      </div>
    </a>
  );
};

const InstagramFeed = () => {
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-scroll animation
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || isPaused) return;

    let scrollPosition = scrollContainer.scrollLeft;
    let animationId;
    const scrollSpeed = 0.4;

    const autoScroll = () => {
      if (isPaused) {
        animationId = requestAnimationFrame(autoScroll);
        return;
      }
      
      scrollPosition += scrollSpeed;
      const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      
      if (scrollPosition >= maxScroll) {
        scrollPosition = 0;
      }
      
      scrollContainer.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(autoScroll);
    };

    const timeoutId = setTimeout(() => {
      animationId = requestAnimationFrame(autoScroll);
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(animationId);
    };
  }, [isPaused]);

  return (
    <section 
      className="py-10 sm:py-14 overflow-hidden"
      style={{ 
        background: 'linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%)'
      }}
    >
      <div className="container mx-auto px-4 mb-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <a 
              href={INSTAGRAM_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 rounded-full p-0.5 transition-transform hover:scale-105"
              style={{ 
                background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)'
              }}
            >
              <img
                src="/petsmart-logo.png"
                alt="petshopnoida"
                className="w-full h-full rounded-full object-cover bg-white border-2 border-white"
              />
            </a>
            <div>
              <a 
                href={INSTAGRAM_PROFILE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-lg sm:text-xl font-bold hover:underline"
                style={{ color: '#262626' }}
              >
                @petshopnoida
              </a>
              <p className="text-xs sm:text-sm" style={{ color: '#8E8E8E' }}>
                Daily puppy updates • Sector 31, Noida
              </p>
            </div>
          </div>
          
          <a
            href={INSTAGRAM_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-button"
            className="px-6 py-2.5 rounded-lg font-semibold text-sm transition-all hover:opacity-90 active:scale-95 flex items-center gap-2"
            style={{
              background: 'linear-gradient(45deg, #405DE6, #5851DB, #833AB4, #C13584, #E1306C, #FD1D1D)',
              color: 'white',
              boxShadow: '0 4px 15px rgba(193, 53, 132, 0.3)'
            }}
          >
            <Instagram size={18} />
            Follow on Instagram
          </a>
        </div>
      </div>

      {/* Feed Grid */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-4 px-4"
        style={{
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setTimeout(() => setIsPaused(false), 3000)}
      >
        {INSTAGRAM_CONTENT.map((post) => (
          <InstagramCard key={post.id} post={post} />
        ))}
      </div>

      {/* View Profile Link */}
      <div className="text-center mt-6 px-4">
        <a
          href={INSTAGRAM_PROFILE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all hover:bg-gray-100"
          style={{ 
            color: '#262626',
            border: '1px solid #DBDBDB'
          }}
        >
          <Instagram size={18} style={{ color: '#C13584' }} />
          View all posts on Instagram
          <ExternalLink size={14} />
        </a>
      </div>

      <style>{`
        /* Hide scrollbar */
        div::-webkit-scrollbar {
          display: none;
        }
        
        /* Mobile adjustments */
        @media (max-width: 640px) {
          .flex-shrink-0 {
            width: 240px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default InstagramFeed;
